import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatSliderModule } from '@angular/material/slider';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ClientClipControllerService, GenerateClipReportRequest, ManagementClipControllerService, ManagementClipResponse } from 'api-clipping';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { take } from 'rxjs/internal/operators/take';
import { UtilsService } from '../../../../../shared/services/utils.service';
import { SharedService } from '../../../../../shared/services/shared.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { environment } from '../../../../../../environments/environment';

@Component({
	selector: 'app-clips-detailed-view',
	standalone: true,
	imports: [MatIconModule, MatButtonModule, MatDialogModule, MatDividerModule, MatSliderModule, FormsModule, TranslateModule, MatProgressSpinnerModule],
	templateUrl: './clips-detailed-view.component.html',
	styleUrl: './clips-detailed-view.component.scss',
})
export class ClipsDetailedViewComponent {
	externalAssets = environment.reseller.externalAssets;
	clipDetails!: ManagementClipResponse;
	apiLoading = false;

	@ViewChild('videoPlayer') videoPlayerRef?: ElementRef<HTMLVideoElement>;

	// TEMP: the generated api-clipping SDK has not been regenerated with videoStartSeconds/
	// videoEndSeconds yet, even though the backend response already carries them. Read them
	// off the raw response via a cast until the SDK model is regenerated.
	private videoStartSecondsFromResponse: number | null = null;
	private videoEndSecondsFromResponse: number | null = null;

	private originalBoundaryStart = 0;
	private originalBoundaryEnd = 0;

	videoDuration = 0;
	boundaryStart = 0;
	boundaryEnd = 0;
	private autoStoppedAtEnd = false;

	readonly minZoom = 1;
	readonly maxZoom = 20;
	zoomLevel = this.minZoom;

	constructor(
		public dialogRef: MatDialogRef<ClipsDetailedViewComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		private apiClipping: ClientClipControllerService,
		public utils: UtilsService,
		private sharedService: SharedService,
		private translate: TranslateService,
		private http: HttpClient
	) {
		if (data.item && data.item.id) {
			this.getClip(data.item.id);
		}
	}

	buttonAction() {}

	onVideoLoadedMetadata() {
		const video = this.videoPlayerRef?.nativeElement;
		if (!video) {
			return;
		}
		this.videoDuration = video.duration || 0;
		this.originalBoundaryStart = this.videoStartSecondsFromResponse ?? 0;
		this.originalBoundaryEnd = this.videoEndSecondsFromResponse ?? this.videoDuration;
		this.boundaryStart = this.originalBoundaryStart;
		this.boundaryEnd = this.originalBoundaryEnd;
		this.zoomLevel = this.minZoom;
		video.currentTime = this.boundaryStart;
	}

	onVideoTimeUpdate() {
		const video = this.videoPlayerRef?.nativeElement;
		if (!video || this.autoStoppedAtEnd) {
			return;
		}
		if (video.currentTime >= this.boundaryEnd) {
			video.pause();
			this.autoStoppedAtEnd = true;
		}
	}

	onVideoSeeked() {
		const video = this.videoPlayerRef?.nativeElement;
		if (!video) {
			return;
		}
		// Re-arms the auto-stop only if the user seeked back before the end boundary;
		// seeking past it on purpose should not immediately pause again.
		this.autoStoppedAtEnd = video.currentTime >= this.boundaryEnd;
	}

	reArmAutoStop() {
		this.autoStoppedAtEnd = false;
	}

	resetBoundaries() {
		this.boundaryStart = this.originalBoundaryStart;
		this.boundaryEnd = this.originalBoundaryEnd;
		this.zoomLevel = this.minZoom;
		this.reArmAutoStop();
	}

	formatTime(totalSeconds: number): string {
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = Math.floor(totalSeconds % 60);
		return `${minutes}:${seconds.toString().padStart(2, '0')}`;
	}

	private parseTimeToSeconds(value: string): number | null {
		const match = value.trim().match(/^(\d+):([0-5]\d)$/);
		if (!match) {
			return null;
		}
		return Number(match[1]) * 60 + Number(match[2]);
	}

	onBoundaryStartTimeChange(event: Event): void {
		const input = event.target as HTMLInputElement;
		const parsed = this.parseTimeToSeconds(input.value);
		if (parsed !== null) {
			this.boundaryStart = Math.min(Math.max(parsed, 0), this.boundaryEnd);
			this.reArmAutoStop();
		}
		input.value = this.formatTime(this.boundaryStart);
	}

	onBoundaryEndTimeChange(event: Event): void {
		const input = event.target as HTMLInputElement;
		const parsed = this.parseTimeToSeconds(input.value);
		if (parsed !== null) {
			this.boundaryEnd = Math.min(Math.max(parsed, this.boundaryStart), this.videoDuration);
			this.reArmAutoStop();
		}
		input.value = this.formatTime(this.boundaryEnd);
	}

	get maxAllowedZoom(): number {
		const boundarySpan = Math.max(this.boundaryEnd - this.boundaryStart, 1);
		return this.videoDuration > 0 ? Math.min(this.maxZoom, this.videoDuration / boundarySpan) : this.maxZoom;
	}

	zoomIn(): void {
		this.zoomLevel = Math.min(this.zoomLevel * 2, this.maxAllowedZoom);
	}

	zoomOut(): void {
		this.zoomLevel = Math.max(this.zoomLevel / 2, this.minZoom);
	}

	onBoundaryBarWheel(event: WheelEvent): void {
		event.preventDefault();
		if (event.deltaY < 0) {
			this.zoomIn();
		} else {
			this.zoomOut();
		}
	}

	private get sliderViewWindow(): { min: number; max: number } {
		if (this.zoomLevel <= this.minZoom || this.videoDuration <= 0) {
			return { min: 0, max: this.videoDuration };
		}
		const windowWidth = this.videoDuration / this.zoomLevel;
		const center = (this.boundaryStart + this.boundaryEnd) / 2;
		let min = center - windowWidth / 2;
		let max = center + windowWidth / 2;
		if (min < 0) {
			max -= min;
			min = 0;
		}
		if (max > this.videoDuration) {
			min -= max - this.videoDuration;
			max = this.videoDuration;
		}
		return { min: Math.max(0, min), max: Math.min(this.videoDuration, max) };
	}

	get sliderMin(): number {
		return this.sliderViewWindow.min;
	}

	get sliderMax(): number {
		return this.sliderViewWindow.max;
	}

	closeDialog() {
		this.dialogRef.close();
	}

	private getClip(id: string) {
		this.apiClipping
			.getClientClipById(id)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.clipDetails = response;
					const rawResponse = response as unknown as { videoStartSeconds?: number | null; videoEndSeconds?: number | null };
					this.videoStartSecondsFromResponse = rawResponse.videoStartSeconds ?? null;
					this.videoEndSecondsFromResponse = rawResponse.videoEndSeconds ?? null;
				},
			});
	}

	getAuthors(item: ManagementClipResponse) {
		if (item?.authors?.length) {
			return item.authors.map((author: any) => this.utils.getParameterByLanguageScript(author, "name")).join(', ');
		}
		return '';
	}

	// PDF report - kept for every medium except TV, where the same button downloads the video clip.
	public generatePdfReport() {
		this.apiLoading = true;
		this.apiClipping
			.generateClientPdfReportForAllClipsByIds({
				clipIds: [this.clipDetails.id],
				lang: this.utils.currentLanguage.toUpperCase() as GenerateClipReportRequest.LangEnum,
			})
			.pipe(take(1))
			.subscribe({
				next: (response: any) => {
					const content = new Blob([response as BlobPart], { type: 'application/pdf' });
					const fileURL = URL.createObjectURL(content);
					window.open(fileURL, '_blank');
					this.apiLoading = false;
				},
				error: err => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
						type: 'ERROR',
					});
					this.apiLoading = false;
				},
			});
	}

	downloadVideoClip(): void {
		if (this.apiLoading || !this.clipDetails?.id) {
			return;
		}
		this.apiLoading = true;

		const startSeconds = Math.round(this.boundaryStart);
		const endSeconds = Math.round(this.boundaryEnd);

		// TEMP: this endpoint isn't in the generated api-clipping SDK yet, so it's called
		// directly via HttpClient until it's regenerated.
		this.http
			.get(`${environment.apiClippingUrl}/v1/client/clips/${this.clipDetails.id}/video-clip`, {
				params: { startSeconds, endSeconds },
				responseType: 'blob',
			})
			.pipe(take(1))
			.subscribe({
				next: (response: Blob) => {
					this.apiLoading = false;
					const fileURL = URL.createObjectURL(response);
					const link = document.createElement('a');
					link.href = fileURL;
					link.download = this.buildClipDownloadFilename(startSeconds, endSeconds);
					link.click();
					URL.revokeObjectURL(fileURL);
				},
				error: (error: HttpErrorResponse) => {
					this.apiLoading = false;
					this.handleVideoClipError(error);
				},
			});
	}

	private buildClipDownloadFilename(startSeconds: number, endSeconds: number): string {
		const mediumName = this.clipDetails?.medium ? this.utils.getParameterByLanguageScript(this.clipDetails.medium, 'name') : '';
		const safeName = (mediumName || 'clip').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '') || 'clip';
		const formatForFilename = (totalSeconds: number) => {
			const minutes = Math.floor(totalSeconds / 60);
			const seconds = Math.floor(totalSeconds % 60);
			return `${minutes}m${seconds.toString().padStart(2, '0')}s`;
		};
		return `${safeName}-${formatForFilename(startSeconds)}-${formatForFilename(endSeconds)}.mp4`;
	}

	private handleVideoClipError(error: HttpErrorResponse): void {
		if (error.error instanceof Blob) {
			error.error.text().then(text => {
				let message = this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR');
				try {
					const parsed = JSON.parse(text);
					if (parsed?.message) {
						message = `${message} \n ${parsed.message}`;
					}
				} catch {
					// response body wasn't JSON, fall back to the generic message
				}
				this.sharedService.showNotification.next({ message, type: 'ERROR' });
			});
			return;
		}

		this.sharedService.showNotification.next({
			message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
			type: 'ERROR',
		});
	}
}
